import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const emptyStep = {
  title: '',
  description: '',
  icon: 'check-circle',
  order: 0,
  active: true,
};

const ProcessManager = () => {
  const [process, setProcess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);

  useEffect(() => {
    fetchProcess();
  }, []);

  const fetchProcess = async () => {
    try {
      const response = await adminApi.getProcess();
      setProcess(response.data);
    } catch (error) {
      console.error('Error fetching process:', error);
      toast.error('Failed to load process steps');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (step = null) => {
    if (step) {
      setEditingStep(step);
    } else {
      setEditingStep({ ...emptyStep, order: process.length });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingStep(null);
  };

  const handleChange = (e) => {
    const value = e.target.name === 'order' ? parseInt(e.target.value) || 0 : e.target.value;
    setEditingStep({ ...editingStep, [e.target.name]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingStep.id) {
        await adminApi.updateProcessStep(editingStep.id, editingStep);
        toast.success('Process step updated');
      } else {
        await adminApi.createProcessStep(editingStep);
        toast.success('Process step created');
      }
      fetchProcess();
      closeDialog();
    } catch (error) {
      console.error('Error saving process:', error);
      toast.error('Failed to save process step');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;
    try {
      await adminApi.deleteProcessStep(id);
      toast.success('Process step deleted');
      fetchProcess();
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error('Failed to delete step');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="process-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Process Steps</h1>
          <p className="text-muted-foreground">Define your workflow methodology.</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-process-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Step
        </Button>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Title</th>
              <th>Icon</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {process.map((step) => (
              <tr key={step.id} data-testid={`process-row-${step.id}`}>
                <td className="font-mono text-primary">{step.order + 1}</td>
                <td className="font-medium">{step.title}</td>
                <td className="text-muted-foreground">{step.icon}</td>
                <td>
                  <span className={step.active ? 'badge-success' : 'badge-error'}>
                    {step.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(step)}
                      data-testid={`edit-process-${step.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(step.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-process-${step.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {process.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted-foreground py-8">
                  No process steps yet. Add your first step.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingStep?.id ? 'Edit Step' : 'Add Step'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Step Title</label>
              <Input
                name="title"
                value={editingStep?.title || ''}
                onChange={handleChange}
                placeholder="Discovery"
                className="input-neon"
                data-testid="process-title-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <Textarea
                name="description"
                value={editingStep?.description || ''}
                onChange={handleChange}
                placeholder="We learn about your business, goals, and requirements"
                rows={3}
                className="input-neon resize-none"
                data-testid="process-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Icon Name</label>
                <Input
                  name="icon"
                  value={editingStep?.icon || ''}
                  onChange={handleChange}
                  placeholder="search"
                  className="input-neon"
                  data-testid="process-icon-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Order</label>
                <Input
                  name="order"
                  type="number"
                  value={editingStep?.order || 0}
                  onChange={handleChange}
                  className="input-neon"
                  data-testid="process-order-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Active</label>
              <Switch
                checked={editingStep?.active ?? true}
                onCheckedChange={(checked) => setEditingStep({ ...editingStep, active: checked })}
                data-testid="process-active-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="ghost" onClick={closeDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-process-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessManager;
